/**
 * Work Order Report Service
 * Handles work order report generation, configuration, and attachments
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { WORK_ORDER_REPORT_ENDPOINTS, buildQueryString } from '../endpoints';

const workOrderReportService = {
  /**
   * Get all work order reports (paginated, admin only)
   * @param {Object} params - Query params (search, status, page, pageSize, sortField, sortDirection)
   * @returns {Promise<Object>} PagedResult of report DTOs
   */
  getAllReports: async (params = {}) => {
    return apiRequest(() =>
      axiosInstance.get(`${WORK_ORDER_REPORT_ENDPOINTS.LIST}${buildQueryString(params)}`)
    );
  },

  /**
   * Get all reports for a work order
   * @param {number} workOrderId
   * @returns {Promise<Array>}
   */
  getReportsForWorkOrder: async (workOrderId) => {
    return apiRequest(() =>
      axiosInstance.get(WORK_ORDER_REPORT_ENDPOINTS.FOR_WORK_ORDER(workOrderId))
    );
  },

  /**
   * Get a specific report by ID
   * @param {number} reportId
   * @returns {Promise<Object>}
   */
  getReportById: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.get(WORK_ORDER_REPORT_ENDPOINTS.GET_BY_ID(reportId))
    );
  },

  /**
   * Get or create a draft report for a work order
   * @param {number} workOrderId
   * @returns {Promise<Object>}
   */
  createDraft: async (workOrderId) => {
    return apiRequest(() =>
      axiosInstance.post(WORK_ORDER_REPORT_ENDPOINTS.CREATE_DRAFT(workOrderId))
    );
  },

  /**
   * Save section configuration and options
   * @param {number} reportId
   * @param {Object} config - { sections, includeCoverPage, includeSectionDividers, includeHeaderFooter }
   * @returns {Promise<Object>}
   */
  configure: async (reportId, config) => {
    return apiRequest(() =>
      axiosInstance.put(WORK_ORDER_REPORT_ENDPOINTS.CONFIGURE(reportId), config)
    );
  },

  /**
   * Queue a report for generation
   * @param {number} reportId
   * @returns {Promise<Object>}
   */
  generate: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.post(WORK_ORDER_REPORT_ENDPOINTS.GENERATE(reportId))
    );
  },

  /**
   * Get signed download URL for a completed report
   * @param {number} reportId
   * @returns {Promise<Object>}
   */
  getDownloadUrl: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.get(WORK_ORDER_REPORT_ENDPOINTS.DOWNLOAD(reportId))
    );
  },

  /**
   * Delete a report
   * @param {number} reportId
   * @returns {Promise<Object>}
   */
  deleteReport: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.delete(WORK_ORDER_REPORT_ENDPOINTS.DELETE(reportId))
    );
  },

  /**
   * Get available sections for a work order (submission reports, certs, attachments)
   * @param {number} workOrderId
   * @returns {Promise<Array>}
   */
  getAvailableSections: async (workOrderId) => {
    return apiRequest(() =>
      axiosInstance.get(WORK_ORDER_REPORT_ENDPOINTS.AVAILABLE_SECTIONS(workOrderId))
    );
  },

  /**
   * Add a supplementary PDF attachment to a work order
   * @param {number} workOrderId
   * @param {Object} dto - { storagePath, fileName, fileSizeBytes, contentType, label }
   * @returns {Promise<Object>}
   */
  addAttachment: async (workOrderId, dto) => {
    return apiRequest(() =>
      axiosInstance.post(WORK_ORDER_REPORT_ENDPOINTS.ADD_ATTACHMENT(workOrderId), dto)
    );
  },

  /**
   * List all attachments for a work order
   * @param {number} workOrderId
   * @returns {Promise<Array>}
   */
  getAttachments: async (workOrderId) => {
    return apiRequest(() =>
      axiosInstance.get(WORK_ORDER_REPORT_ENDPOINTS.LIST_ATTACHMENTS(workOrderId))
    );
  },

  /**
   * Delete an attachment
   * @param {number} attachmentId
   * @returns {Promise<Object>}
   */
  deleteAttachment: async (attachmentId) => {
    return apiRequest(() =>
      axiosInstance.delete(WORK_ORDER_REPORT_ENDPOINTS.DELETE_ATTACHMENT(attachmentId))
    );
  },
};

export default workOrderReportService;
